/**
 * SoleadLab 2.0 — IV Analysis Panel
 */

import { useState } from 'react';
import { useIVStore } from '@/stores/ivStore';

interface Props {
  sessionId: string;
}

export default function IVAnalysisPanel({ sessionId }: Props) {
  const measurements = useIVStore((s) => s.measurements);
  const findings = useIVStore((s) => s.findings);
  const runAnalysis = useIVStore((s) => s.runAnalysis);

  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const includedCount = measurements.filter((m) => m.included === 1).length;
  const lowIrrCount = measurements.filter(
    (m) => m.included === 1 && m.irradiance != null && m.irradiance < 200
  ).length;

  const handleAnalyze = async () => {
    setRunning(true);
    setError('');
    try {
      await runAnalysis(sessionId);
    } catch (err) {
      setError(String(err));
    } finally {
      setRunning(false);
    }
  };

  const criticals = findings.filter((f) => f.severity === 'critical');
  const warnings = findings.filter((f) => f.severity === 'warning');
  const infos = findings.filter((f) => f.severity === 'info');

  // Measurement label lookup
  const measurementLabels = new Map(measurements.map((m) => [m.id, m.label]));

  return (
    <div>
      <div className="iv-note iv-note--info">
        Only included measurements ({includedCount} of {measurements.length}) are analyzed.
        Toggle inclusion in the Measurements tab.
      </div>

      <div style={{ marginBottom: 16 }}>
        <button
          className="iv-btn iv-btn--primary"
          onClick={handleAnalyze}
          disabled={running || includedCount === 0}
        >
          {running ? 'Analyzing...' : 'Run Analysis'}
        </button>
        {includedCount === 0 && (
          <span style={{ marginLeft: 8, fontSize: 'var(--sl-text-xs)', color: 'var(--text-muted)' }}>
            No included measurements
          </span>
        )}
      </div>

      {error && <div className="iv-note iv-note--error">{error}</div>}

      {lowIrrCount > 0 && (
        <div className="iv-note">
          ⚠ {lowIrrCount} included measurement{lowIrrCount > 1 ? 's were' : ' was'} taken under low irradiance (&lt;200 W/m²).
          Comparative analysis for {lowIrrCount > 1 ? 'those rows' : 'that row'} may be less reliable.
        </div>
      )}

      {findings.length > 0 && (
        <div>
          <div style={{ marginBottom: 12, fontSize: 'var(--sl-text-sm)', color: 'var(--text-secondary)' }}>
            {findings.length} finding(s): {criticals.length} critical, {warnings.length} warnings, {infos.length} info
          </div>

          <FindingsGroup label="Critical" items={criticals} severity="critical" measurementLabels={measurementLabels} />
          <FindingsGroup label="Warning" items={warnings} severity="warning" measurementLabels={measurementLabels} />
          <FindingsGroup label="Info" items={infos} severity="info" measurementLabels={measurementLabels} />
        </div>
      )}

      {findings.length === 0 && measurements.length > 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 'var(--sl-text-sm)' }}>
          No findings yet. Click "Run Analysis" to analyze included measurements.
        </div>
      )}
    </div>
  );
}

function FindingsGroup({
  label,
  items,
  severity,
  measurementLabels,
}: {
  label: string;
  items: Array<{ id: string; title: string; description: string; measurement_id: string | null }>;
  severity: string;
  measurementLabels: Map<string, string>;
}) {
  if (items.length === 0) return null;

  const color = severity === 'critical' ? 'var(--red)' : severity === 'warning' ? 'var(--orange)' : 'var(--primary-blue)';

  return (
    <div className="iv-findings-group">
      <div className="iv-findings-group__title" style={{ color }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block' }} />
        {label} ({items.length})
      </div>
      {items.map((f) => {
        const mLabel = f.measurement_id ? measurementLabels.get(f.measurement_id) : null;
        return (
          <div key={f.id} className={`iv-finding-card iv-finding-card--${severity}`}>
            <div className="iv-finding-card__title">{f.title}</div>
            <div className="iv-finding-card__desc">{f.description}</div>
            {mLabel && <div className="iv-finding-card__measurement">Measurement: {mLabel}</div>}
          </div>
        );
      })}
    </div>
  );
}
